/**
 * Auth + gating context.
 *
 * FREE-TIER MODEL: OTP sign-in comes FIRST; the `users/{uid}` doc is created by
 * the user themselves on the post-OTP Registration screen (no approval creates
 * it). So "signed in but no doc yet" now means "needs to register".
 *
 * Tracks the Firebase auth user AND the user's own `users/{uid}` doc (which the
 * rules let a signed-in user read for themselves). The combination drives
 * routing (feature #1):
 *   - no firebase user                     → Login/Splash
 *   - firebase user, no users doc          → needs Registration (post-OTP)
 *   - users doc status 'pending'           → under-review
 *   - users doc status 'blocked'/'rejected'→ blocked
 *   - users doc status 'approved'          → Home (products readable)
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { onAuthStateChanged, signOut, type User as FbUser } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import type { User, UserStatus } from '@mitang/shared';
import { paths, SESSION_MAX_AGE_MS } from '@mitang/shared';
import { auth, db } from '../../lib/firebase';
import { setActivityUser, flushNow } from '../../lib/activity/activityLogger';
import { setPresenceUser } from '../../lib/presence';
import { getCachedToken } from '../../lib/push';
import { removeOwnFcmToken } from '../../lib/repo';

export type AuthPhase =
  | 'loading'
  | 'signedOut'
  /** Signed in via OTP but has NO users/{uid} doc yet → must register. */
  | 'needsRegistration'
  | 'signedIn';

export interface AuthState {
  phase: AuthPhase;
  fbUser: FbUser | null;
  /** The user's own users/{uid} doc, or null if it doesn't exist yet. */
  profile: User | null;
  /** Coarse status derived from profile; 'unknown' when no doc yet. */
  status: UserStatus | 'unknown';
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [phase, setPhase] = useState<AuthPhase>('loading');
  const [fbUser, setFbUser] = useState<FbUser | null>(null);
  const [profile, setProfile] = useState<User | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setFbUser(user);
      setActivityUser(user?.uid ?? null);
      if (!user) {
        setPresenceUser(null);
        setProfile(null);
        setProfileLoaded(true);
        setPhase('signedOut');
      } else {
        setPhase('signedIn');
        setProfileLoaded(false);
      }
    });
    return () => unsub();
  }, []);

  // SESSION EXPIRY: dealers must re-OTP every 10 days. The Firebase token's
  // authTime is the moment of the last real sign-in (tamper-resistant);
  // checked on auth-change and hourly for long-lived tabs.
  useEffect(() => {
    if (!fbUser) return;
    let cancelled = false;
    const check = async (): Promise<void> => {
      try {
        const res = await fbUser.getIdTokenResult();
        const authTime = Date.parse(res.authTime);
        if (!cancelled && Number.isFinite(authTime) && Date.now() - authTime > SESSION_MAX_AGE_MS) {
          await signOut(auth);
        }
      } catch {
        // Offline — retry on the next tick.
      }
    };
    void check();
    const t = setInterval(() => void check(), 60 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [fbUser]);

  // Subscribe to the user's own doc while signed in.
  useEffect(() => {
    if (!fbUser) return;
    const ref = doc(db, paths.user(fbUser.uid));
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setProfile(snap.exists() ? (snap.data() as User) : null);
        setProfileLoaded(true);
      },
      () => {
        // Read denied / offline before doc exists → treat as no profile.
        setProfile(null);
        setProfileLoaded(true);
      },
    );
    return () => unsub();
  }, [fbUser]);

  const status: UserStatus | 'unknown' = profile?.status ?? 'unknown';

  // Feed the presence manager the approved user's identity (denormalized so the
  // admin Live monitor needs no users read). AppLifecycle drives the heartbeat.
  useEffect(() => {
    if (profile && profile.status === 'approved') {
      setPresenceUser({
        uid: profile.id,
        name: profile.name,
        email: profile.email,
        companyName: profile.companyName,
        city: profile.city,
      });
    }
  }, [profile]);

  const logout = useMemo(
    () => async () => {
      await flushNow();
      // FREE-TIER MODEL: unregister this device's FCM token by removing it from
      // the caller's own users/{uid} doc BEFORE signing out (once signed out the
      // rules would deny the write). Best-effort — never block logout on it.
      const uid = auth.currentUser?.uid;
      if (uid) {
        const token = await getCachedToken();
        if (token) {
          await removeOwnFcmToken(uid, token).catch(() => undefined);
        }
      }
      await signOut(auth);
    },
    [],
  );

  const value = useMemo<AuthState>(() => {
    // While signed in but profile not yet loaded, stay in 'loading' so we don't
    // flash the wrong screen. Once loaded: no doc → needsRegistration.
    let effectivePhase: AuthPhase = phase;
    if (fbUser) {
      if (!profileLoaded) effectivePhase = 'loading';
      else if (!profile) effectivePhase = 'needsRegistration';
    }
    return { phase: effectivePhase, fbUser, profile, status, logout };
  }, [phase, fbUser, profile, profileLoaded, status, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
